import { ReadonlyVec2, vec2 } from 'gl-matrix';
import { PlaneView } from '../../view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../EventHandlerResult';
import { ModifierKeys } from '../ModifierKeys';
import { ToolAction } from '../ToolAction';

/**
 * {@link WindowingAction} provides functionality to modify the {@link WindowingLevel._level level} of a {@link WindowingLevel}-object.
 */
export class WindowingAction extends ToolAction {
    private previousCoordinates?: ReadonlyVec2 = undefined;

    private stepSize = 3;

    public constructor(private readonly eventTarget: EventTarget) {
        super();
    }

    /**
     * MousePress event that was caught by the canvas and propagated by {@link ToolManager} to this {@link WindowingAction}.\
     * This sets the {@link WindowingAction.activeEventState activeEventState} and starts a {@link PanAction} if the event is successfully handled.
     * @param button The button that triggered the event
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link PlaneView} on which the event occured
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.START_ACTION} if
     * 1. the {@link button} equals {@link WindowingAction.mouseButton},
     * 2. the {@link modifierKeys} equal {@link WindowingAction.modifierKeys} and
     * 3. the {@link view} is an instanceof {@link PlaneView},
     *
     * in which case the event was successfully handled and {@link WindowingAction} was started.
     *
     * If any of the conditions is not met, the event is not handled and {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} is returned.
     */
    public onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        _coordinates: ReadonlyVec2,
        _view: PlaneView
    ): EventHandlerResult {
        this.previousCoordinates = vec2.clone(_coordinates);
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /**
     * MouseMove event that was caught by the canvas and propagated by {@link ToolManager} to this {@link WindowingAction}.\
     * This updates the {@link windowingLevel}-object's {@link WindowingLevel._level level} if the event is successfully handled.
     * @param newCoordinates The mouse position in client pixel coordinates at the time of the event
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_HANDLED EVENT_HANDLED} if
     * the {@link activeEventState} is defined, in which case the event was successfully handled.\
     * {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} if the event was not handled.
     */
    public onMouseMove(newCoordinates: ReadonlyVec2): EventHandlerResult {
        if (this.previousCoordinates) {
            const detail = vec2.create();
            if (this.previousCoordinates[0] < newCoordinates[0]) {
                detail[0] = this.stepSize;
            } else if (this.previousCoordinates[0] > newCoordinates[0]) {
                detail[0] = -this.stepSize;
            }
            if (this.previousCoordinates[1] < newCoordinates[1]) {
                detail[1] = this.stepSize;
            } else if (this.previousCoordinates[1] > newCoordinates[1]) {
                detail[1] = -this.stepSize;
            }
            if (detail[0] !== 0 || detail[1] !== 0) {
                this.eventTarget.dispatchEvent(
                    new CustomEvent<vec2>('windowLevel_mousemove', { detail })
                );
            }

            this.previousCoordinates = newCoordinates;
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * MouseRelease event that was caught by the canvas and propagated by {@link ToolManager} to this {@link WindowingAction}.\
     * If this {@link WindowingAction} is active, in which case it has a {@link WindowingAction.activeEventState activeEventState},\
     * the {@link windowingLevel}-object's {@link WindowingLevel._level level} value is updated, the {@link WindowingAction.activeEventState activeEventState} is erased,
     * this {@link WindowingAction} is finished and {@link EventHandlerResult} of type {@link EventHandlerResultType.FINISH_ACTION FINISH_ACTION} is returned.
     *
     * If {@link WindowingAction.activeEventState activeEventState} is undefined {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} is returned.
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.FINISH_ACTION FINISH_ACTION} if {@link PanAction.activeEventState activeEventState} is defined,\
     * {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} otherwise.
     */
    public onMouseRelease(): EventHandlerResult {
        this.previousCoordinates = undefined;
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }
}
